import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import TextInputField from "../common/TextInputField";
import ActionButton from "../../../../common/ui/ActionButton";
import PlusSvg from "../../../../common/svgs/PlusSvg";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import CheckSingleIcon from "../../svg/CheckSingleIcon";
import RoundedIconContainer from "../common/RoundedIconContainer";
import MoreOptionsPopover, { MenuOption } from "../common/MoreOptionsPopover";
import MoreIcon from "../../svg/MoreIcon";
import { KeragonWorkflow } from "../../../../common/entities/keragon";
import {
  addKeragonWorkflow,
  deleteKeragonWorkflow,
} from "../../../../common/network/Api";
import {
  getDefaultWorkflow,
  nextActiveFlag,
  planDefaultSwitch,
  planDelete,
  validateWorkflowForm,
  WorkflowForm,
  WorkflowFormErrors,
} from "./keragonWorkflowRules";

/**
 * Settings → "Keragon sync": the affiliate's Keragon webhook connections
 * (Figma 16762:470932 empty, 16765:472718 form, 16767:474187 list).
 *
 * Same bordered card as the InBody integration above it. Three states:
 * - empty  → header + the three "how to connect" steps;
 * - form   → name + webhook URL, Cancel/Save (add or edit);
 * - list   → one row per workflow with a "Default" pill on the active one and
 *            a ⋯ menu (Set as default / Edit / Delete).
 * Exactly one workflow is meant to be the default (`active === 1`); switching
 * it asks for confirmation (spec G3), and deleting it promotes another so the
 * "Sync EHR data" option never loses its preselection.
 */

const HOW_TO_STEPS = [
  {
    number: 1,
    title: "Build the workflow",
    subtitle: "Create a workflow that pushes results out of your EHR.",
  },
  {
    number: 2,
    title: "Copy its webhook URL",
    subtitle: "The URL Keragon generates is the connection.",
  },
  {
    number: 3,
    title: "Paste it here",
    subtitle: "Name it so you recognise it later, then connect.",
  },
];

const EMPTY_FORM: WorkflowForm = { name: "", URL: "" };

// Nearest ancestor that actually scrolls (the shell's ScrollableWrapper).
const getScrollParent = (el: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const KeragonSyncSettings = () => {
  const theme: Theme = useTheme();
  const {
    ASKeragonWorkflows,
    ASIsLoadingKeragonWorkflows,
    ASLoadKeragonWorkflows,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KeragonWorkflow | null>(null);
  const [form, setForm] = useState<WorkflowForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const formRowRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isMutating, setIsMutating] = useState(false);

  const workflows = ASKeragonWorkflows;
  const isInitialLoading = ASIsLoadingKeragonWorkflows && workflows.length === 0;

  useEffect(() => {
    ASLoadKeragonWorkflows();
  }, [ASLoadKeragonWorkflows]);

  // The form row appends under the list (add) or replaces a row (edit), which
  // can sit below the fold on a long page. An add scrolls the page to the
  // bottom (the form is the last thing on it, and "nearest" left the fields
  // clipped); an edit centres its row. Focus the name so typing can start.
  useEffect(() => {
    if (!formOpen) return;
    const row = formRowRef.current;
    if (row) {
      if (editing) {
        row.scrollIntoView({ block: "center", behavior: "smooth" });
      } else {
        const scroller = getScrollParent(row);
        scroller?.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
      }
    }
    nameInputRef.current?.focus();
  }, [formOpen, editing]);

  const notify = (title: string, message: string, type: string) => {
    ASSetSnackBarMessage({ title, message });
    ASSetTypeSnackBar(type);
    ASSetOpenSnackBar(true);
  };

  const confirm = (
    title: string,
    message: string,
    confirmLabel: string,
    onConfirm: () => void
  ) => {
    ASSetSnackBarMessage({ title, message });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({ confirmLabel, onConfirm });
    ASSetOpenSnackBar(true);
  };

  // Force past the cache and tell the other tabs (open client workspaces) so
  // their "Sync EHR data" option unlocks/relocks immediately.
  const reload = () => ASLoadKeragonWorkflows({ force: true, broadcast: true });

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (workflow: KeragonWorkflow) => {
    setEditing(workflow);
    setForm({ name: workflow.name, URL: workflow.URL });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleFieldChange = (key: keyof WorkflowForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Validated live: the save icon is disabled while anything is wrong, and a
  // field shows its message once it has content (the "required" cases just
  // keep the icon disabled rather than nagging an untouched field).
  const validation: WorkflowFormErrors = validateWorkflowForm(
    form,
    workflows,
    editing?.id ?? null
  );
  const shownErrors: WorkflowFormErrors = {
    name: form.name.trim() ? validation.name : undefined,
    URL: form.URL.trim() ? validation.URL : undefined,
  };
  const canSave = !validation.name && !validation.URL && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      // companyID is resolved server-side from the token for a new row (it is
      // the company id, NOT the affiliate's pnoe_user_id — sending that 400s);
      // an edit keeps the stored one.
      await addKeragonWorkflow({
        id: editing?.id ?? null,
        companyID: editing?.companyID || null,
        name: form.name.trim(),
        URL: form.URL.trim(),
        active: nextActiveFlag(workflows, editing),
      });
      await reload();
      closeForm();
      notify(
        "Connection saved",
        "Connection saved and ready to search.",
        "success"
      );
    } catch (error) {
      console.error("Error saving Keragon workflow:", error);
      notify("Save failed", "We couldn't save this connection. Please try again.", "alert");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = (workflow: KeragonWorkflow) => {
    const plan = planDefaultSwitch(workflows, workflow.id);
    if (!plan) return;
    const current = getDefaultWorkflow(workflows);
    const message = current
      ? `"${current.name}" will stop syncing. New searches will use "${workflow.name}" instead.`
      : `New searches will use "${workflow.name}".`;

    confirm("Set as default connection", message, "Set as default", async () => {
      setIsMutating(true);
      let partial = false;
      try {
        await addKeragonWorkflow({ ...plan.activate, active: 1 });
        for (const previous of plan.deactivate) {
          try {
            await addKeragonWorkflow({ ...previous, active: 0 });
          } catch (error) {
            console.error("Error deactivating Keragon workflow:", error);
            partial = true;
          }
        }
        if (partial) {
          notify(
            "Default partly updated",
            "The previous default couldn't be deactivated. Set the default again to fix it.",
            "alert"
          );
        } else {
          notify("Default updated", `"${workflow.name}" is now the default connection.`, "success");
        }
      } catch (error) {
        console.error("Error setting default Keragon workflow:", error);
        notify("Update failed", "We couldn't change the default connection. Please try again.", "alert");
      } finally {
        await reload();
        setIsMutating(false);
      }
    });
  };

  const handleDelete = (workflow: KeragonWorkflow) => {
    const plan = planDelete(workflows, workflow.id);
    if (!plan) return;
    const message = plan.promote
      ? `"${workflow.name}" will be removed and "${plan.promote.name}" becomes the default connection.`
      : `"${workflow.name}" will be removed. Stored results are not affected.`;

    confirm("Delete connection", message, "Delete", async () => {
      setIsMutating(true);
      try {
        await deleteKeragonWorkflow(workflow.id);
        if (plan.promote) {
          await addKeragonWorkflow({ ...plan.promote, active: 1 });
        }
        notify("Connection deleted", `"${workflow.name}" was removed.`, "success");
      } catch (error) {
        console.error("Error deleting Keragon workflow:", error);
        notify("Delete failed", "We couldn't delete this connection. Please try again.", "alert");
      } finally {
        await reload();
        setIsMutating(false);
      }
    });
  };

  const menuOptionsFor = (workflow: KeragonWorkflow): MenuOption[] => [
    ...(workflow.active === 1
      ? []
      : [{ label: "Set as default", onClick: () => handleSetDefault(workflow) }]),
    { label: "Edit", onClick: () => openEdit(workflow) },
    { label: "Delete", onClick: () => handleDelete(workflow) },
  ];

  const renderSkeletonRows = () => (
    <Box sx={styles.skeletonRows}>
      {Array.from({ length: 2 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          height={44}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Box>
  );

  // Inline form: an edit replaces its own row so the other connections stay
  // visible; an add appends a form row under the list. The two inputs sit in
  // the Name / Webhook URL columns and the save (✓) / cancel (✕) icons in
  // the actions column, mirroring the Readings side panel's add-value row.
  const hasShownError = Boolean(shownErrors.name || shownErrors.URL);
  const renderFormRow = () => (
    <Box key="form" ref={formRowRef} sx={styles.formRow(theme, hasShownError)}>
      <Box sx={styles.nameCell}>
        <TextInputField
          label="Integration name"
          placeholder="Integration name"
          value={form.name}
          name="keragonName"
          inputRef={nameInputRef}
          error={shownErrors.name}
          handleChange={(e) => handleFieldChange("name", e.target.value)}
          disabled={isSaving}
        />
      </Box>
      <Box sx={styles.urlCell}>
        <TextInputField
          label="Keragon webhook URL"
          placeholder="Keragon webhook URL"
          value={form.URL}
          name="keragonURL"
          error={shownErrors.URL}
          handleChange={(e) => handleFieldChange("URL", e.target.value)}
          disabled={isSaving}
        />
      </Box>
      <Box sx={styles.actionsCell}>
        <RoundedIconContainer isDisabled={!canSave} handleClick={handleSave}>
          <CheckSingleIcon
            size={16}
            fill={
              canSave
                ? theme.colors_ds.PRIMARY_ICON_MAIN
                : theme.colors_ds.NEUTRAL_ICON_WEAK
            }
          />
        </RoundedIconContainer>
        <RoundedIconContainer isDisabled={isSaving} handleClick={closeForm}>
          <XIconSvg />
        </RoundedIconContainer>
      </Box>
    </Box>
  );

  const renderWorkflowRow = (workflow: KeragonWorkflow) => (
    <Box key={workflow.id} sx={styles.listRow(theme)}>
      <Box sx={{ ...styles.cellText(theme), ...styles.nameCell }}>
        {workflow.name}
      </Box>
      <Box
        sx={{ ...styles.cellText(theme), ...styles.urlCell }}
        title={workflow.URL}
      >
        {workflow.URL}
      </Box>
      <Box sx={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
        {workflow.active === 1 && (
          <Box sx={styles.defaultBadge(theme)}>Default</Box>
        )}
        <MoreOptionsPopover
          minWidth="160px"
          options={menuOptionsFor(workflow)}
          icon={<MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />}
        />
      </Box>
    </Box>
  );

  const showList = workflows.length > 0 || formOpen;

  return (
    <Box sx={styles.container(theme)}>
      {/* Header */}
      <Box sx={styles.headerRow}>
        <Box sx={styles.labelSection}>
          <Box sx={styles.titleWrapper(theme)}>Keragon sync</Box>
          <Box sx={styles.subtitleWrapper(theme)}>
            Connect a Keragon webhook to bring their EHR results into their
            biomarkers.
          </Box>
        </Box>
        {!formOpen && (
          <ActionButton
            label="Add connection"
            icon={<PlusSvg />}
            onClick={openAdd}
            isDisable={isInitialLoading || isMutating}
            sx={styles.addButton}
          />
        )}
      </Box>

      {/* Empty state — how to connect */}
      {!showList && (
        <Box sx={styles.stepsRow(theme)}>
          {isInitialLoading
            ? renderSkeletonRows()
            : HOW_TO_STEPS.map((step) => (
                <Box key={step.number} sx={styles.step}>
                  <Box sx={styles.stepNumber(theme, step.number)}>
                    {step.number}
                  </Box>
                  <Box sx={styles.stepText}>
                    <Box sx={styles.titleWrapper(theme)}>{step.title}</Box>
                    <Box sx={styles.subtitleWrapper(theme)}>{step.subtitle}</Box>
                  </Box>
                </Box>
              ))}
        </Box>
      )}

      {/* Workflow list, with the add/edit form inline */}
      {showList && (
        <Box sx={styles.list(theme)}>
          {workflows.length > 0 && (
            <Box sx={styles.listHeader(theme)}>
              <Box sx={{ ...styles.listHeaderCell(theme), ...styles.nameCell }}>
                Name
              </Box>
              <Box sx={{ ...styles.listHeaderCell(theme), ...styles.urlCell }}>
                Webhook URL
              </Box>
              <Box sx={styles.actionsCell} />
            </Box>
          )}
          {isMutating ? (
            <Box sx={{ padding: "12px 16px" }}>{renderSkeletonRows()}</Box>
          ) : (
            <>
              {workflows.map((workflow) =>
                formOpen && editing?.id === workflow.id
                  ? renderFormRow()
                  : renderWorkflowRow(workflow)
              )}
              {formOpen && !editing && renderFormRow()}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default KeragonSyncSettings;

const styles = {
  container: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colorTokens.N[50]}`,
    borderRadius: "8px",
    width: "100%",
    display: "flex",
    flexDirection: "column" as "column",
  }),
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
  },
  labelSection: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    gap: "4px",
  },
  titleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
  }),
  subtitleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
  addButton: {
    flexShrink: 0,
    width: "auto",
    height: "34px",
    fontSize: "12px",
    padding: "8px 12px",
  },
  // Empty state — the three numbered steps in one divided row.
  stepsRow: (theme: Theme) => ({
    display: "flex",
    gap: "24px",
    padding: "16px",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
  }),
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    flex: 1,
    minWidth: 0,
  },
  // Figma tints each step circle blue / yellow / green (P10 / W10 / S10).
  stepNumber: (theme: Theme, number: number) => ({
    width: "24px",
    height: "24px",
    borderRadius: "12px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colorTokens.N[80],
    backgroundColor:
      number === 1
        ? theme.colorTokens.P[10]
        : number === 2
        ? theme.colorTokens.W[10]
        : theme.colorTokens.S[10],
  }),
  stepText: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    minWidth: 0,
  },
  // Add / edit form — an inline list row: inputs in the Name / URL columns,
  // ✓ / ✕ icons in the actions column. Extra bottom room only while a field
  // shows its error, which hangs below the input.
  formRow: (theme: Theme, hasError: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: hasError ? "12px 16px 32px" : "12px 16px",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
    backgroundColor: theme.colorTokens.N[20],
    "&:first-of-type": { borderTop: "none" },
  }),
  // List
  list: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
  }),
  listHeader: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "6px 16px",
    // Figma #F2F4F7 has no token; N30 is the nearest neutral tint.
    backgroundColor: theme.colorTokens.N[30],
  }),
  listHeaderCell: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "18px",
    letterSpacing: "1px",
    textTransform: "capitalize" as "capitalize",
    color: theme.colorTokens.N[80],
  }),
  listRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px 16px",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
    "&:first-of-type": { borderTop: "none" },
  }),
  cellText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[90],
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  nameCell: { flex: 1, minWidth: 0 },
  urlCell: { flex: 2, minWidth: 0 },
  actionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px",
    width: "140px",
    flexShrink: 0,
    // The ⋯ popover must not be clipped by the row.
    overflow: "visible",
  },
  // Same pill as the InBody "Connected" badge, so the two cards match.
  defaultBadge: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "19px",
    whiteSpace: "nowrap" as "nowrap",
    color: theme.colorTokens.N[100],
    backgroundColor: theme.colorTokens.S[10],
    border: `1px solid ${theme.colorTokens.S[70]}`,
  }),
  skeletonRows: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    width: "100%",
  },
};
